import { vec3 } from 'gl-matrix'
import Program from 'nanogl/program'
import Texture2D from 'nanogl/texture-2d'
import Chunk from 'nanogl-pbr/Chunk'
import ChunksSlots from 'nanogl-pbr/ChunksSlots'
import Input, { ShaderType, Uniform } from 'nanogl-pbr/Input'
import TexCoord from 'nanogl-pbr/TexCoord'

import { TimeEntity } from '@/webgl/runner/entities/Time'

export default class PortalSpiralChunk extends Chunk {
  private timeInput: Input
  private uvs: Input
  private texture: Texture2D
  private time: Uniform
  private visiblityInput: Input

  color1: vec3 = vec3.fromValues(0, 0.161, 0.22)
  color2: vec3 = vec3.fromValues(0.008, 0.282, 0.376)
  visibility: Uniform
  rotateFactor: number = 1

  constructor(readonly timeEntity: TimeEntity) {
    super(true, true)
    this.addChild((this.timeInput = new Input('Time', 2, ShaderType.FRAGMENT)))
    this.addChild((this.uvs = new Input('TexCoord', 2, ShaderType.FRAGMENT)))
    this.addChild((this.visiblityInput = new Input('Visibility', 3, ShaderType.FRAGMENT)))

    this.uvs.attachAttribute(TexCoord.create('aTexCoord0').attrib)
    this.time = this.timeInput.attachUniform('uTime', 2)
    this.visibility = this.visiblityInput.attachUniform('uVisibility', 3)
    this.visibility.set(1, 1, 1)
  }

  setTexture(texture: Texture2D) {
    this.texture = texture
  }

  setup(prg: Program): void {
    prg.tSpiralTexture(this.texture)
    prg.uColor1(...this.color1)
    prg.uColor2(...this.color2)
    this.time.set(this.timeEntity.time, this.rotateFactor)
  }

  protected _genCode(slots: ChunksSlots): void {
    slots.add(
      'pf',
      /*glsl */ `
      uniform sampler2D tSpiralTexture;
      uniform vec3 uColor1;
      uniform vec3 uColor2;

      vec2 getUV(float scale, float rotation){
        vec2 uv = (TexCoord() - 0.5);// * scale + 0.5;
        float s = sin(rotation);
        float c = cos(rotation);
        mat2 rot = mat2(c, -s, s, c);
        uv = rot * uv * scale + 0.5;
        return uv;
      }
    `
    )

    slots.add(
      'postf',
      /*glsl*/ `

      const float s1 = 4.0;
      const float s2 = 2.0;
      const float s3 = 1.0;

      float r = 0.7 * Time().y;

      vec2 uv1 = getUV(s1, Time().x * r);
      vec2 uv2 = getUV(s2, -Time().x * r - 0.5 * Time().y);
      vec2 uv3 = getUV(s3, Time().x * r + 0.3 * Time().y);

      float c1 = texture2D(tSpiralTexture, uv1).r * Visibility().x;
      float c2 = texture2D(tSpiralTexture, uv2).r * Visibility().y;
      float c3 = texture2D(tSpiralTexture, uv3).r * Visibility().z;

      c1 = step(0.5, c1);
      c2 = step(0.5, c2);
      c3 = step(0.5, c3);

      float ic1 = 1.0 - c1;
      float ic2 = 1.0 - c2;
      float ic3 = 1.0 - c3;

      vec3 color;
      color += uColor1 * c1;
      color += uColor2 * c2 * ic1;
      color += uColor1 * c3 * ic1 * ic2;
      color += uColor2 * ic1 * ic2 * ic3;

      FragColor.rgb = color;
    `
    )
  }
}
